import { Clock, Loader2 } from "lucide-react";
import { QueueBadge, safeQueueKey, useQueueBadgeStatus } from "@/components/grooming/queue-status-badge";
import { useMe } from "@/hooks/queries/use-me";
import type { GroomingPreparing } from "@/lib/api/grooming";
import { cn } from "@/lib/utils";

/**
 * "Being prepared by Aaron · started 9:40" (epic PI-SPEC-SW-E283): someone
 * is preparing this item in a running grooming batch. Informational only:
 * nothing is ever hidden or disabled because someone else is working on it,
 * so this marker never wraps or replaces a control. The time is shown in the
 * viewer's time zone (their SpecTree profile's, else this computer's).
 *
 * Grooming contract 3 (epic PI-SPEC-SW-E284) says whether the item is still
 * waiting in that person's run queue ("Queued by Aaron") or being briefed
 * now ("Being prepared by Aaron · started 9:40"). Given the item's project
 * and id, the marker also shows this computer's own queue badge
 * ("Queued · #3", "Running"); for the viewer's own work the local badge
 * replaces the server marker, so it is not said twice. In a browser and
 * with a contract-2 app only the server marker shows.
 */

/** The start time as the viewer's clock shows it (for example "9:40 AM"); empty when unknown. Pure; exported for tests. */
export function preparingTime(startedAt: string | null, timeZone?: string): string {
  if (!startedAt) return "";
  const date = new Date(startedAt);
  if (Number.isNaN(date.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
  try {
    return date.toLocaleTimeString(undefined, timeZone ? { ...options, timeZone } : options);
  } catch {
    // An unknown time zone name: fall back to this computer's.
    return date.toLocaleTimeString(undefined, options);
  }
}

/** True when the viewer is the person preparing the item. Pure; exported for tests. */
export function preparingIsMine(preparing: GroomingPreparing, viewerId: string | undefined): boolean {
  return viewerId !== undefined && preparing.byUserId !== null && preparing.byUserId === viewerId;
}

/** The marker's words. Pure; exported for tests. */
export function preparingText(preparing: GroomingPreparing, viewerId: string | undefined, timeZone?: string): string {
  const name = preparing.byName?.trim() ?? "";
  const who = preparingIsMine(preparing, viewerId) ? "you" : name !== "" ? name : "someone";
  if (preparing.state === "queued") return `Queued by ${who}`;
  const time = preparingTime(preparing.startedAt, timeZone);
  return time ? `Being prepared by ${who} · started ${time}` : `Being prepared by ${who}`;
}

export interface PreparingMarkerProps {
  preparing: GroomingPreparing | null | undefined;
  /** E284: the item's project and id, to show this computer's queue badge too. */
  project?: string | undefined;
  itemId?: string | undefined;
  className?: string | undefined;
}

/** The marker, the local queue badge, both, or nothing. */
export function PreparingMarker({ preparing, project, itemId, className }: PreparingMarkerProps) {
  const { data: me } = useMe();
  const local = useQueueBadgeStatus(project && itemId ? safeQueueKey("groom-item", project, itemId) : null);
  const localActive = local !== null && (local.kind === "queued" || local.kind === "running");
  const mine = preparing ? preparingIsMine(preparing, me?.id) : false;
  const showServer = preparing !== null && preparing !== undefined && !(mine && localActive);
  if (!showServer && !local) return null;

  const text = showServer ? preparingText(preparing, me?.id, me?.timeZone ?? undefined) : "";
  const Icon = preparing?.state === "queued" ? Clock : Loader2;
  return (
    <span className={cn("inline-flex min-w-0 flex-wrap items-center gap-1.5 text-xs", className)} data-testid="item-queue-status">
      {local && <QueueBadge status={local} className="h-[18px] px-1.5 text-[10.5px]" />}
      {showServer && (
        <span
          className="inline-flex min-w-0 items-center gap-1 text-sky-300"
          title={text}
          data-testid="preparing-marker"
          data-state={preparing.state ?? "running"}
        >
          <Icon
            className={cn("h-3 w-3 flex-none", preparing.state !== "queued" && "motion-safe:animate-spin")}
            aria-hidden="true"
          />
          <span className="truncate">{text}</span>
        </span>
      )}
    </span>
  );
}
